import { Component, computed, inject, input } from '@angular/core';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { Store } from '@ngrx/store';

import { Permissions, processHorizonRedirect, selectUserContext } from '@app/modules/authentication/store/auth';
import { IconComponent, IconProperties, IconTypes } from '@app/shared/components/icon';
import { AppHeaderTheme } from '../../app-header-theme.model';
import { TranslatePipe } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { selectOrganization } from '@app/modules/core/store/user-profile';
import { UserPreferencesFacade } from '@app/core/stores/user-preferences';
import { NgTemplateOutlet } from '@angular/common';

interface MenuItem {
  id: string;
  label: string;
  iconProps?: IconProperties;
  route?: string;
}

interface AppMenuSection {
  id: string;
  title?: string;
  items: MenuItem[];
}

const defaultIconStyles: IconProperties['style'] = {
  height: '1.5rem',
  width: '1.5rem',
  fontSize: '1.25rem',
  color: 'var(--neutral-color-600)'
};

const mainModuleIconStyles: IconProperties['style'] = {
  ...defaultIconStyles,
  height: '2.5rem',
  width: '2.5rem',
  color: 'var(--text-color-4)',
  borderRadius: '0.375rem'
};

@Component({
  selector: 'topo-app-header-menu',
  imports: [CdkMenu, CdkMenuItem, CdkMenuTrigger, IconComponent, TranslatePipe, RouterLink, NgTemplateOutlet],
  templateUrl: './app-header-menu.component.html',
  styleUrls: ['./app-header-menu.component.scss', '../app-header-button.scss']
})
export class AppHeaderMenuComponent {
  theme = input<AppHeaderTheme>(AppHeaderTheme.Default);
  protected isDarkMode = inject(UserPreferencesFacade).isDarkTheme;

  protected isActive = false;

  menuTriggerIconProps: IconProperties = {
    type: IconTypes.SystemIcon,
    path: 'icon-menu_grid',
    style: {
      fontSize: '1.5rem',
      fontWeight: '400'
    }
  };

  private store = inject(Store);
  private userContext = this.store.selectSignal(selectUserContext);
  private userOrg = this.store.selectSignal(selectOrganization);
  menuSections = computed<AppMenuSection[]>(() => {
    const userContext = this.userContext();

    if (!userContext) {
      return;
    }

    const sections: AppMenuSection[] = [];

    // Main Modules
    if (
      userContext.hasPermission(Permissions.MapRead) ||
      userContext.hasPermission(Permissions.IncidentRead) ||
      userContext.hasPermission(Permissions.MassCommunicationsRead) ||
      userContext.hasPermission(Permissions.AnalyticsRead)
    ) {
      const items: MenuItem[] = [];

      if (userContext.hasPermission(Permissions.MapRead)) {
        items.push({
          id: 'map',
          label: 'MANAGEMENT.MENU.OPERATING-PICTURE',
          route: '/cop',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-map_object',
            style: {
              ...mainModuleIconStyles,
              backgroundColor: 'var(--primary-color-600)'
            }
          }
        });
      }

      if (userContext.hasPermission(Permissions.IncidentRead)) {
        items.push({
          id: 'incidents',
          label: 'MANAGEMENT.MENU.INCIDENTS',
          route: '/incidents',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-briefcase',
            style: {
              ...mainModuleIconStyles,
              backgroundColor: 'var(--accent-color-500)'
            }
          }
        });
      }

      if (userContext.hasPermission(Permissions.MassCommunicationsRead)) {
        items.push({
          id: 'communications',
          label: 'MANAGEMENT.MENU.COMMUNICATIONS',
          route: '/communications',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-bullhorn',
            style: {
              ...mainModuleIconStyles,
              backgroundColor: 'var(--primary-color-700)'
            }
          }
        });
      }

      if (userContext.hasPermission(Permissions.AnalyticsRead)) {
        items.push({
          id: 'analytics',
          label: 'MANAGEMENT.MENU.ANALYTICS',
          route: '/analytics',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-chart',
            style: {
              ...mainModuleIconStyles,
              backgroundColor: 'var(--secondary-color-600)'
            }
          }
        });
      }

      sections.push({
        id: 'main_module',
        items
      });
    }

    // Horizon SSO
    if (this.userOrg()?.configurationSettings?.horizonSSORedirectEnabled) {
      sections.push({
        id: 'horizon_sso',
        items: [
          {
            id: 'horizon_link',
            label: 'MANAGEMENT.MENU.HORIZON'
          }
        ]
      });
    }

    // Data Boss
    if (
      userContext.hasPermission(Permissions.PipelineCreate) ||
      userContext.hasPermission(Permissions.DatasetCreate) ||
      userContext.hasPermission(Permissions.WorkflowCreate) ||
      userContext.hasPermission(Permissions.RatingScalesRead)
    ) {
      const items: MenuItem[] = [];

      if (userContext.hasPermission(Permissions.PipelineCreate)) {
        items.push({
          id: 'pipelines',
          label: 'MANAGEMENT.MENU.PIPELINES',
          route: '/management/datafeeds',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-pipe',
            style: defaultIconStyles
          }
        });
      }

      if (userContext.hasPermission(Permissions.DatasetCreate)) {
        items.push({
          id: 'datasets',
          label: 'MANAGEMENT.MENU.DATASETS',
          route: '/management/datasets',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-dataset',
            style: defaultIconStyles
          }
        });
      }

      if (userContext.hasPermission(Permissions.WorkflowCreate)) {
        items.push({
          id: 'workflow_statuses',
          label: 'MANAGEMENT.MENU.ALERT-STATUSES',
          route: '/management/status',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-small_workflow',
            style: defaultIconStyles
          }
        });
      }

      if (userContext.hasPermission(Permissions.RatingScalesRead)) {
        items.push({
          id: 'rating_scales',
          label: 'MANAGEMENT.MENU.RATING-SCALES',
          route: '/management/ratings',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-rating',
            style: defaultIconStyles
          }
        });
      }

      sections.push({
        id: 'data_boss',
        title: 'MANAGEMENT.MENU.DATA-BOSS',
        items
      });
    }

    // Workbench
    if (userContext.hasPermission(Permissions.MapUpdate)) {
      sections.push({
        id: 'workbench',
        title: 'MANAGEMENT.MENU.WORKBENCH',
        items: [
          {
            id: 'map_designer',
            label: 'MANAGEMENT.MENU.MAP-DESIGNER',
            route: '/management/maps',
            iconProps: {
              type: IconTypes.SystemIcon,
              path: 'icon-compass',
              style: defaultIconStyles
            }
          }
        ]
      });
    }

    // Communication Center
    if (
      !userContext.hasPermission(Permissions.MassCommunicationsRead) &&
      (userContext.hasPermission(Permissions.ChannelConfigCreate) ||
        userContext.hasPermission(Permissions.DistributionListCreate) ||
        userContext.hasPermission(Permissions.CommunicationTemplateCreate))
    ) {
      const items: MenuItem[] = [];

      if (userContext.hasPermission(Permissions.ChannelConfigCreate)) {
        items.push({
          id: 'channels',
          label: 'MANAGEMENT.MENU.CHANNELS',
          route: '/management/communication-center/channels',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-satellite-dish',
            style: defaultIconStyles
          }
        });
      }

      if (userContext.hasPermission(Permissions.DistributionListCreate)) {
        items.push({
          id: 'distribution_lists',
          label: 'MANAGEMENT.MENU.DISTRIBUTION-LISTS',
          route: '/management/communication-center/distribution-lists',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-people-group',
            style: defaultIconStyles
          }
        });
      }

      if (userContext.hasPermission(Permissions.CommunicationTemplateCreate)) {
        items.push({
          id: 'communication_templates',
          label: 'MANAGEMENT.MENU.TEMPLATES',
          route: '/management/communication-center/templates',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-template',
            style: defaultIconStyles
          }
        });
      }

      sections.push({
        id: 'communication_center',
        title: 'MANAGEMENT.MENU.COMMUNICATION-CENTER',
        items
      });
    }

    // Community
    if (userContext.hasPermission(Permissions.OrganizationLinksCreate)) {
      sections.push({
        id: 'community_section',
        title: 'MANAGEMENT.MENU.COMMUNITY',
        items: [
          {
            id: 'community',
            label: 'MANAGEMENT.MENU.COMMUNITY',
            route: '/management/community',
            iconProps: {
              type: IconTypes.SystemIcon,
              path: 'icon-handshake-1',
              style: defaultIconStyles
            }
          }
        ]
      });
    }

    // Admin
    if (
      userContext.hasPermission(Permissions.OrganizationRead) ||
      userContext.hasPermission(Permissions.UserProfileCreate) ||
      userContext.hasPermission(Permissions.SecurityGroupCreate)
    ) {
      const items: MenuItem[] = [];

      if (userContext.hasPermission(Permissions.UserProfileCreate) || userContext.hasPermission(Permissions.SecurityGroupCreate)) {
        const securityRouterLink = userContext.hasPermission(Permissions.UserProfileUpdate) ? 'users' : 'groups';

        items.push({
          id: 'security',
          label: 'MANAGEMENT.MENU.SECURITY',
          route: `/management/security/${securityRouterLink}`,
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-security',
            style: defaultIconStyles
          }
        });
      }

      if (userContext.hasPermission(Permissions.OrganizationUpdate)) {
        items.push({
          id: 'organizations',
          label: 'MANAGEMENT.MENU.ORGANIZATIONS',
          route: `/management/organizations`,
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-building',
            style: defaultIconStyles
          }
        });
      }

      sections.push({
        id: 'admin',
        title: 'MANAGEMENT.MENU.ADMIN',
        items
      });
    }

    return sections;
  });

  generateHorizonRedirect(): void {
    this.store.dispatch(processHorizonRedirect());
  }
}
